import React, { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../theme';
import { useThemedStyles } from '../hooks/useThemedStyles';

// Only import on native — avoids web bundle errors
let NativeDateTimePicker: any = null;
if (Platform.OS !== 'web') {
  NativeDateTimePicker = require('@react-native-community/datetimepicker').default;
}

interface DatePickerFieldProps {
  label: string;
  value?: Date | null;
  onChange: (date: Date | null) => void;
  placeholder?: string;
  minimumDate?: Date;
  maximumDate?: Date;
  /** 'date' (default) shows only a date picker; 'datetime' additionally shows
   * a time picker (native: a second spinner step after date is picked; web:
   * a datetime-local input) — used by columns that need to store a specific
   * time-of-day, not just a calendar date. */
  mode?: 'date' | 'datetime';
  /** Shrinks padding/font/min-height so the field can sit flush at the same
   * height as a compact filter-chip row (e.g. a filter bar) instead of the
   * default full-size form-field box. Purely cosmetic — same behavior. */
  compact?: boolean;
  /** Drops the field's own border/background — for compact mode when the
   * caller supplies a single shared seamless border around a group of two
   * fields (e.g. a from/to date range) instead of each field boxed on its own. */
  bare?: boolean;
}

const fmt = (d: Date, withTime: boolean) =>
  withTime
    ? d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });

export const DatePickerField: React.FC<DatePickerFieldProps> = ({
  label,
  value,
  onChange,
  placeholder = 'No date set',
  minimumDate,
  maximumDate,
  mode = 'date',
  compact = false,
  bare = false,
}) => {
  const s = useThemedStyles(getS);
  const withTime = mode === 'datetime';
  const [show, setShow] = useState(false);
  const [pendingDate, setPendingDate] = useState<Date | null>(null); // native datetime: holds the date leg while the time leg is picked
  const [showTimeStep, setShowTimeStep] = useState(false);

  const toWebInputValue = (d: Date | null | undefined) => {
    if (!d) return '';
    const iso = d.toISOString();
    return withTime ? iso.slice(0, 16) : iso.slice(0, 10);
  };

  if (Platform.OS === 'web') {
    // RN Web's TextInput hardcodes the underlying DOM input's type to "text"
    // and does not forward a `type` prop — passing type="date"/"datetime-local"
    // to it silently does nothing, leaving a free-text field with no real
    // picker. A raw HTML <input> is required here to get the browser's actual
    // native date/time picker UI (matches the pattern CalendarForm.tsx used
    // successfully before being migrated to this component).
    return (
      <View style={s.field}>
        {!!label && <Text style={s.label}>{label}</Text>}
        <View style={s.webRow}>
          <input
            type={withTime ? 'datetime-local' : 'date'}
            value={toWebInputValue(value)}
            min={minimumDate ? toWebInputValue(minimumDate) : undefined}
            max={maximumDate ? toWebInputValue(maximumDate) : undefined}
            onChange={(e: any) => {
              const v = e.target.value;
              if (!v) {
                onChange(null);
                return;
              }
              const d = new Date(v);
              if (!isNaN(d.getTime())) onChange(d);
            }}
            style={bare ? bareWebInputStyle : compact ? compactWebInputStyle : (webInputStyle as any)}
          />
          {value && (
            <TouchableOpacity onPress={() => onChange(null)} style={s.clearBtn}>
              <Ionicons name="close-circle" size={compact ? 14 : 18} color={colors.textLight} />
            </TouchableOpacity>
          )}
        </View>
      </View>
    );
  }

  return (
    <View style={s.field}>
      {!!label && <Text style={s.label}>{label}</Text>}
      <TouchableOpacity
        style={[s.picker, compact && s.pickerCompact, bare && s.pickerBare]}
        onPress={() => { setPendingDate(value ?? new Date()); setShowTimeStep(false); setShow(true); }}
        activeOpacity={0.7}
      >
        <Ionicons name={withTime ? 'time-outline' : 'calendar-outline'} size={compact ? 13 : 16} color={colors.textLight} />
        <Text style={[s.pickerText, compact && s.pickerTextCompact, !value && s.placeholder]} numberOfLines={1}>
          {value ? fmt(value, withTime) : placeholder}
        </Text>
        {value && (
          <TouchableOpacity onPress={() => onChange(null)} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
            <Ionicons name="close-circle" size={compact ? 13 : 16} color={colors.textLight} />
          </TouchableOpacity>
        )}
      </TouchableOpacity>

      {show && NativeDateTimePicker && (
        <NativeDateTimePicker
          value={pendingDate ?? value ?? new Date()}
          mode={!withTime ? 'date' : showTimeStep ? 'time' : 'date'}
          display={Platform.OS === 'ios' ? 'spinner' : 'default'}
          minimumDate={minimumDate}
          maximumDate={maximumDate}
          onChange={(_: any, selected?: Date) => {
            if (!selected) {
              setShow(false);
              return;
            }
            if (!withTime) {
              setShow(Platform.OS === 'ios');
              onChange(selected);
              return;
            }
            if (!showTimeStep) {
              // Date leg done — move to the time leg (Android needs the picker
              // re-opened for the second mode; iOS keeps a single spinner open).
              setPendingDate(selected);
              if (Platform.OS === 'ios') {
                setShowTimeStep(true);
              } else {
                setShowTimeStep(true);
                setShow(true);
              }
            } else {
              setShow(Platform.OS === 'ios');
              onChange(selected);
            }
          }}
        />
      )}
    </View>
  );
};

const getS = () => StyleSheet.create({
  field: { gap: spacing.xs },
  label: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    fontWeight: '600',
    fontFamily: typography.fontFamily.semibold,
  },
  picker: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
    backgroundColor: colors.background.surface,
  },
  pickerCompact: {
    borderRadius: 999,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    gap: spacing.xs / 2,
  },
  pickerBare: {
    borderWidth: 0,
    backgroundColor: 'transparent',
    paddingHorizontal: spacing.xs,
  },
  pickerText: {
    flex: 1,
    fontSize: typography.fontSize.base,
    color: colors.textDark,
    fontFamily: typography.fontFamily.regular,
  },
  pickerTextCompact: {
    fontSize: typography.fontSize.sm,
  },
  placeholder: { color: colors.textLight },
  // Web
  webRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs },
  clearBtn: { padding: spacing.xs },
});

const webInputStyle = {
  flex: 1,
  border: `1px solid ${colors.border as string}`,
  borderRadius: 8,
  paddingLeft: spacing.sm,
  paddingRight: spacing.sm,
  paddingTop: spacing.sm,
  paddingBottom: spacing.sm,
  fontSize: typography.fontSize.base,
  color: colors.textDark as string,
  backgroundColor: colors.background.surface as string,
  fontFamily: typography.fontFamily.regular,
  minHeight: 48,
};

const compactWebInputStyle = {
  ...webInputStyle,
  borderRadius: 999,
  paddingLeft: spacing.sm,
  paddingRight: spacing.sm,
  paddingTop: 4,
  paddingBottom: 4,
  fontSize: typography.fontSize.sm,
  minHeight: 0,
  height: 30,
};

const bareWebInputStyle = {
  ...compactWebInputStyle,
  border: 'none',
  backgroundColor: 'transparent',
  paddingLeft: spacing.xs,
  paddingRight: spacing.xs,
};
